import React, { useRef, useEffect, useState } from "react";
import cx from "classnames";
import { Link } from 'react-router-dom';
import moment from "moment";

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import {checkCounterHasExpired} from "../../services/checkCounterHasExpired";
import {useLazyGetOrdersCountQuery} from "../../api/order";
import OrdersCount from "../ordersCount/OrdersCount";

import Logo from '../../static/icons/logo.svg';
import LogoBlack from '../../static/icons/logo-black.svg';
import LogoMobileWhite from '../../static/icons/logo_mobile_white.svg';
import LogoMobileBlack from '../../static/icons/logo_mobile_black.svg';
import ButtonPrimary from "../button-primary/ButtonPrimary";
import Counter from "../counter/Counter";
import ArrowBg from "../arrow-bg/ArrowBg";
import {VEDMARKET_LINKS} from "../../constants";

import styles from './Header.module.scss';

const links = [
  { href: '#features', text: 'Особенности' },
  { href: '#how-to', text: 'Как это работает' },
  { href: '#faq', text: 'Вопросы-ответы' },
  { href: '/blog', text: 'Блог' },
  { href: 'https://my.vedmarket.ru/company/login', text: 'Партнерам', isNewTab: true },
  { href: '#contacts', text: 'Контакты' },
];

type HeaderProps = {
  handleOpen: () => void;
  isAgreement?: boolean;
};

function Header({isAgreement}: HeaderProps) {

  const headerRef = useRef<HTMLDivElement | null>(null);

  const [showHeaderScroll, setShowHeaderScroll] = useState(false);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const [displayCount, setDisplayCount] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

  const [transitionIsActive, setTransitionIsActive] = useState(true);

  const lastScrollY = useRef(0);

  const counterHasExpired = checkCounterHasExpired();

  const [ ordersCountRequest ] = useLazyGetOrdersCountQuery()

  useEffect(() => {
    ordersCountRequest().then(data => {
      setDisplayCount(data.data?.order_count ? String(data.data.order_count) : '');
    });
  }, [ordersCountRequest]);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;

      const headerBottom = headerRef.current.getBoundingClientRect().bottom;
      const currentScrollY = window.scrollY;
      const headerOutOfView = headerBottom < 0;
      const scrollingUp = currentScrollY < lastScrollY.current;

      if (headerOutOfView && scrollingUp) {
        setShowHeaderScroll(true);
      } else {
        setShowHeaderScroll(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (menuIsOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [menuIsOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenMenu = () => {
    setMenuIsOpen(true);
  }

  const handleCloseMenu = () => {
    setMenuIsOpen(false);
  }

  const handleClickMobileLink = () => {
    handleCloseMenu();
    setTransitionIsActive(false)

    setTimeout(function() {
      setTransitionIsActive(true)
    }, 1000);
  }

  const isAnchorLink = (href: string) => href.startsWith('#');

  const shouldShowRightPart = !isAgreement && (counterHasExpired ? displayCount : true);

  return (
      <>
        <div className={cx(styles.headerScroll, {
          [styles.headerScrollVisible]: showHeaderScroll,
          [styles.headerScrollHidden]: !showHeaderScroll
        })}>
          <Container className={styles.headerContainer}>
            <Row className="align-items-center justify-content-between">
              <Col xs={4}>
                <a href="https://vedmarket.ru/" className={styles.logoContainer}>
                  <img src={isMobile ? LogoMobileBlack : LogoBlack} alt='Вэдмаркет' className={cx(styles.logoScroll, styles.logo)}/>
                </a>
              </Col>
              <Col xl={8} className={styles.menu}>
                <Row className="align-items-center justify-content-between">
                  <Col xs={9} className="pe-0" >
                    <nav className={styles.navbar}>
                      <ul className={styles.navbarList}>
                        <li className={styles.navLink}>
                          <a href="#features" className={styles.link}>Особенности</a>
                        </li>
                        <li className={styles.navLink}>
                          <a href="#how-to" className={styles.link}>Как это работает</a>
                        </li>
                        <li className={styles.navLink}>
                          <a href="#faq" className={styles.link}>Вопросы-ответы</a>
                        </li>
                        <li className={styles.navLink}>
                          <Link to="/blog" className={styles.link}>Блог</Link>
                        </li>
                        <li className={styles.navLink}>
                          <Link to="https://my.vedmarket.ru/company/login" target='_blank' className={styles.link}>Партнерам</Link>
                        </li>
                        <li className={styles.navLink}>
                          <a href="#contacts" className={styles.link}>Контакты</a>
                        </li>
                      </ul>
                    </nav>
                  </Col>
                  <Col xs={3} className="pe-0 ps-0">
                    <div className={styles.buttonContainer}>
                      <ButtonPrimary href={VEDMARKET_LINKS.login} target="_blank" rel="noreferrer" label="Войти"/>
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col xs={2} className={cx(styles.menuContainer, {
                [styles.menuContainerOpen] : menuIsOpen,
              })}>
                <button type="button" className={styles.menuButton} onClick={menuIsOpen ? handleCloseMenu : handleOpenMenu} aria-label={menuIsOpen ? 'Закрыть меню' : 'Открыть меню'}>
                  {menuIsOpen ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                  </svg> : <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.17383 18H21.1738V16H3.17383V18ZM3.17383 13H21.1738V11H3.17383V13ZM3.17383 6V8H21.1738V6H3.17383Z" fill="currentColor"/>
                  </svg>}
                </button>
              </Col>
            </Row>
          </Container>
        </div>
        <div className={cx({
          [styles.headerScrollMenuIsOpen]: menuIsOpen
        })}>
          <div className={cx(styles.menuContainerMobile, {
            [styles.menuContainerMobileTransition] : transitionIsActive,
            [styles.menuContainerMobileActive] : menuIsOpen
          })}>
            <Container className={styles.headerContainer}>
              <Row>
                <Col xs={12}>
                  <nav className={styles.navbarMobile}>
                    <ul className={styles.navbarListMobile}>
                      {links.map(({ href, text, isNewTab }) =>
                          <li key={href} className={styles.navLinkMobile}>
                            {isAnchorLink(href) ? <a href={href} className={styles.linkMobile} key={href} onClick={handleClickMobileLink} rel="noreferrer" target={isNewTab ? '_blank' : '_self'}>
                              {text}
                            </a> : <Link to={href} className={styles.linkMobile} onClick={handleClickMobileLink} target={isNewTab ? '_blank' : '_self'}>
                              {text}
                            </Link>}
                          </li>
                      )}
                    </ul>
                  </nav>
                </Col>
              </Row>
              <Row>
                <Col lg={4}>
                  <ButtonPrimary href={VEDMARKET_LINKS.login} target="_blank" rel="noreferrer" label="Войти" className={styles.buttonMobileOpen}/>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
        <ArrowBg>
          <div className={styles.header} ref={headerRef}>
            <Container className={styles.headerContainer}>
              <Row className="align-items-center justify-content-between">
                <Col lg={4}>
                  <a href="https://vedmarket.ru/" className={styles.logoContainer}>
                    <img src={isMobile ? LogoMobileWhite : Logo} alt='Вэдмаркет' className={styles.logo}/>
                  </a>
                </Col>
                <Col xl={8} className={styles.menu}>
                  <Row className="align-items-center justify-content-between">
                    <Col xs={9} className="pe-0" >
                      <nav className={styles.navbar}>
                        <ul className={styles.navbarList}>
                          {links.map(({ href, text }) =>
                              <li key={href} className={styles.navLink}>
                                {isAnchorLink(href) ? (
                                    <a href={href} className={styles.link} key={href}>
                                      {text}
                                    </a>
                                ) : (
                                    <Link to={href} className={styles.link} key={href}>
                                      {text}
                                    </Link>
                                )}
                              </li>
                          )}
                        </ul>
                      </nav>
                    </Col>
                    <Col xs={3} className="pe-0 ps-0">
                      <div className={styles.buttonContainer}>
                        <ButtonPrimary href={VEDMARKET_LINKS.login} target="_blank" rel="noreferrer" label="Войти"/>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col xl={isAgreement ? 9 : 6 }>
                  <h1 className={cx(styles.title, {
                    [styles.titleAgreement]: isAgreement,
                  })}>
                    {isAgreement ? 'Согласие на обработку персональных данных' : 'Новый стандарт международной торговли'}
                  </h1>
                </Col>
                {shouldShowRightPart && (
                    <Col xl={6}>
                      <h2 className={styles.titleDemo}>
                        {counterHasExpired ? 'Сформировано расчетов' : 'До запуска демо-версии осталось'}
                      </h2>
                      {counterHasExpired ? <OrdersCount count={displayCount} /> : <Counter/>}
                      {counterHasExpired && <h2 className={styles.footerTitleDemo}>
                        На {moment().format('DD.MM.YYYY HH:mm')}
                      </h2>}
                    </Col>
                )}
                {displayCount === '' && (
                    <Col xl={6} className={styles.emptyCount}/>
                )}
              </Row>
              {!isAgreement && <>
                <Row>
                  <Col lg={6}>
                    <h2 className={styles.subtitle}>
                      Простой и&nbsp;понятный расчет финальной стоимости импортных товаров у&nbsp;себя на&nbsp;складе
                    </h2>
                  </Col>
                </Row>
                <Row>
                  <Col lg={4}>
                    <ButtonPrimary href={VEDMARKET_LINKS.demo} target="_blank" rel="noreferrer" className={{
                      [styles.buttonTry]: counterHasExpired
                    }} label="Посмотреть демо">
                      <>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                        </svg>
                        Посмотреть демо
                      </>
                    </ButtonPrimary>
                  </Col>
                </Row>
              </>}
            </Container>
          </div>
        </ArrowBg>
      </>
  );
}

export default Header;
